import * as React from 'react'

import { ChevronDown } from 'lucide-react'

import { IWorkflowStep, WorkflowStepType } from '~/client/graph'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { IListSelectorItem } from '~/client/src/shared/components/ListSelector/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

interface IStepItem extends IListSelectorItem {
  label: string
}

interface IProps {
  allWorkflowSteps: IWorkflowStep[]
  selectedStepIds: string[]
  onApply(stepIds: string[]): void
}

const searchFn = (item: IStepItem, query: string): boolean =>
  item.label.toLowerCase().includes(query.trim().toLowerCase())

export default function NotificationStepPicker({
  allWorkflowSteps,
  selectedStepIds,
  onApply,
}: IProps) {
  const [open, setOpen] = React.useState(false)

  const nonRecurringSteps = React.useMemo(
    () =>
      allWorkflowSteps.filter(
        s => s.type !== WorkflowStepType.RecurringInspection,
      ),
    [allWorkflowSteps],
  )

  const items: IStepItem[] = React.useMemo(
    () =>
      nonRecurringSteps.map(step => {
        const realPosition =
          allWorkflowSteps.findIndex(s => s.id === step.id) + 1
        const level = getWorkflowStepLevel(step.id, allWorkflowSteps)
        const name = getWorkflowStepDisplayName(step.type, level)
        return {
          id: step.id,
          label: `${realPosition}. ${name}`,
        }
      }),
    [nonRecurringSteps, allWorkflowSteps],
  )

  const selectedIds = React.useMemo(
    () => new Set(selectedStepIds),
    [selectedStepIds],
  )

  const allSelected =
    items.length > 0 && items.every(item => selectedIds.has(item.id))

  const handleApply = React.useCallback(
    (draft: Set<string>) => {
      onApply(Array.from(draft))
      setOpen(false)
    },
    [onApply],
  )

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [])

  const handleChipDismiss = React.useCallback(
    (stepId: string) => {
      onApply(selectedStepIds.filter(id => id !== stepId))
    },
    [selectedStepIds, onApply],
  )

  const selectedItems = React.useMemo(
    () => items.filter(item => selectedIds.has(item.id)),
    [items, selectedIds],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            className={cn(
              'tw:flex tw:items-center tw:gap-1 tw:min-h-8 tw:px-2 tw:py-1',
              'tw:rounded-md tw:border tw:border-input tw:bg-background',
              'tw:text-body-md tw:text-foreground',
              'tw:cursor-pointer tw:hover:bg-muted tw:transition-colors',
              'tw:w-full tw:flex-wrap',
            )}
          />
        }
      >
        {allSelected ? (
          <span>{Localization.translator.allSteps}</span>
        ) : (
          selectedItems.map(item => (
            <Tag key={item.id} onDismiss={() => handleChipDismiss(item.id)}>
              {item.label}
            </Tag>
          ))
        )}
        <ChevronDown className="tw:size-4 tw:text-muted-foreground tw:ml-auto tw:shrink-0" />
      </PopoverTrigger>
      <PopoverContent
        className="tw:w-[400px] tw:h-[360px] tw:max-h-(--available-height) tw:p-0 tw:gap-0"
        align="start"
      >
        <ListSelector.Root<IStepItem>
          items={items}
          selectedIds={selectedIds}
          draft
          onClose={handleClose}
          search={{ searchFn }}
          className="tw:flex-1 tw:min-h-0"
        >
          <ListSelector.Title
            title={Localization.translator.selectWorkflowSteps}
            onClose={handleClose}
          />
          <ListSelector.Toolbar />
          <ListSelector.Counter
            formatTotal={Localization.translator.xStepsCount}
          />
          <ListSelector.List<IStepItem>
            renderItemContent={item => (
              <span className="tw:truncate">{item.label}</span>
            )}
          />
          <ListSelector.Actions onApply={handleApply} onClose={handleClose} />
        </ListSelector.Root>
      </PopoverContent>
    </Popover>
  )
}
